import React from "react";

export default function ServicesApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Services API — Fetch Services
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Retrieve the catalog of available services with optional filtering.
            Results are scoped by the caller’s role and customer visibility.
          </p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Method
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                POST (list/filter)
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/services/
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            This endpoint returns active services, optionally filtered by name
            and customer. Visibility is role-aware:
          </p>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              <span className="font-semibold">Customer users:</span> receive
              services mapped to their company. If no mapping exists, only
              active, non-special services are returned. When a mapping exists,
              only <code className="font-mono">public</code> services in that
              mapping are returned.
            </li>
            <li>
              <span className="font-semibold">Staff / internal users:</span>{" "}
              receive all active services (subject to filters).
            </li>
            <li>Name filtering is case-insensitive and partial </li>
          </ul>
        </section>

        {/* Request */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Request
          </h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">HTTP</div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <span className="font-semibold">Method:</span>{" "}
                  <code className="font-mono">POST</code>
                </li>
                <li>
                  <span className="font-semibold">URL:</span>{" "}
                  <code className="font-mono">/api/services/</code>
                </li>
                <li>
                  <span className="font-semibold">Headers:</span>
                  <br />
                  <code className="font-mono">
                    Authorization: JWT your_token
                  </code>
                  <br />
                  <code className="font-mono">
                    Content-Type: application/json
                  </code>
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Body schema
              </div>
              <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">name</code>{" "}
                  <span className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>{" "}
                  — <span className="font-mono">string</span>. Case-insensitive
                  substring match on service name.
                </li>
                <li>
                  <code className="font-mono">customer_id</code>{" "}
                  <span className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>{" "}
                  — <span className="font-mono">number | string</span>. The
                  customer identifier to evaluate service mappings/visibility.
                </li>
              </ul>
            </div>
          </div>

          {/* Request example (restyled) */}
          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  POST
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                /api/services/
              </span>
            </div>

            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`POST https://api-livetakeoff.herokuapp.com/api/services/
Authorization: JWT your_access_token
Content-Type: application/json

{
  "name": "detail",
  "customer_id": 1234
}`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Response */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Response
          </h2>

          <div className="mt-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Envelope
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                The endpoint returns a list of service objects. If pagination is
                enabled, the response uses the standard DRF keys:
              </p>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">count</code> — total items
                </li>
                <li>
                  <code className="font-mono">next</code> — URL for next page or{" "}
                  <code className="font-mono">null</code>
                </li>
                <li>
                  <code className="font-mono">previous</code> — URL for previous
                  page or <code className="font-mono">null</code>
                </li>
                <li>
                  <code className="font-mono">results</code> — array of services
                </li>
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-6">
              <div className="text-sm font-semibold text-slate-900">
                Service object (fields &amp; types)
              </div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">id</code> —{" "}
                  <span className="font-mono">number</span>{" "}
                </li>
                <li>
                  <code className="font-mono">name</code> —{" "}
                  <span className="font-mono">string</span>{" "}
                </li>
                <li>
                  <code className="font-mono">short_name</code> —{" "}
                  <span className="font-mono">string</span>{" "}
                </li>
                <li>
                  <code className="font-mono">description</code> —{" "}
                  <span className="font-mono">string</span>{" "}
                </li>
                <li>
                  <code className="font-mono">short_description</code> —{" "}
                  <span className="font-mono">string</span>{" "}
                </li>
                <li>
                  <code className="font-mono">category</code> —{" "}
                  <span className="font-mono">string</span>{" "}
                </li>
              </ul>
            </div>
          </div>

          {/* Response example (restyled) */}
          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Response
              </h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  200
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
              <span className="font-mono text-xs text-zinc-400">
                application/json
              </span>
            </div>

            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`[
  {
    "id": 101,
    "name": "Full Carpet Extraction Detail",
    "short_name": "Carpet Extraction",
    "description": "Description of Full Carpet Extraction Detail",
    "short_description": "Short description of Full Carpet Extraction Detail",
    "category": "I"
  },
  {
    "id": 102,
    "name": "Full Exterior Detail",
    "short_name": "Exterior Details",
    "description": "Description of Full Exterior Detail",
    "short_description": "Short description of Full Exterior Detail",
    "category": "E"
  },
  
]`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Usage snippets */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Usage examples
          </h2>

          <div className="mt-4 grid gap-4 sm:grid-cols-1">
            {/* curl (restyled) */}
            <div className="not-prose my-2 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  curl
                </h3>
              </div>

              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    POST
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">
                  /api/services/
                </span>
              </div>

              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`curl -X POST https://api.livetakeoff.com/api/services/ \\
  -H "Authorization: JWT your_access_token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "detail",
    "customer_id": 1234
  }'`}
                  </pre>
                </div>
              </div>
            </div>

            {/* fetch (restyled) */}
            <div className="not-prose my-2 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  fetch (JavaScript)
                </h3>
              </div>

              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    POST
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500" />
                <span className="font-mono text-xs text-zinc-400">
                  /api/services/
                </span>
              </div>

              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`const res = await fetch("https://api.livetakeoff.com/api/services/", {
  method: "POST",
  headers: {
    "Authorization": "JWT " + token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    name: "detail",
    customer_id: 1234
  })
});

const data = await res.json();`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Validation / rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Validation & rules
          </h2>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              Request body is optional. Send an empty JSON object to fetch the
              default list for your role.
            </li>
            <li>
              <code className="font-mono">customer_id</code> is evaluated
              against service–customer mappings; unmapped customer users will
              receive only active, non-special services.
            </li>
            <li>
              Results are always ordered by{" "}
              <code className="font-mono">name</code>.
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
